import { FC, useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import Image from 'react-bootstrap/Image';

import { BsList } from 'react-icons/bs';
import { FaArchive } from 'react-icons/fa';
import styled from 'styled-components';
import md5 from 'md5';
import { useTranslation } from 'react-i18next';

import logo from 'assets/ehr-logo-short.svg';

import Button from 'components/Button';
import LanguageSwitch from 'components/LanguageSwitch';
import { useActiveClientContext } from 'store/active-client';
import { getSubdomain, isActiveRoute } from 'utils/helpers/routing';
import TooltipWrapper from 'components/TooltipWrapper';
import { usePermissionsContext } from 'store/permissions';
import { PermissionKeys } from 'store/types';
import { useWizardContext } from 'store/wizard';
import { buildClientUrl } from 'utils/helpers/client';
import tenantLogoConfig from 'utils/tenantLogoConfig';
import useArchiveData from 'utils/helpers/useArchiveData';
import Profile from './ProfileHeader';
import Dropdown from './MenuDropdown';
import { IHeaderProps } from './types';
import HeaderMobile from './HeaderMobile';

const HeaderComponent: FC<IHeaderProps> = ({ menuItems, showLangSwitch, isClientAdmin }: IHeaderProps) => {
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const { client, removeActiveClient } = useActiveClientContext();
  const history = useHistory();
  const location = useLocation();
  const subdomain = getSubdomain();
  const { t } = useTranslation();
  const { checkPermission } = usePermissionsContext();
  const { resetWizardState } = useWizardContext();

  // Disable scroll if mobile menu is open
  useEffect(() => {
    const body = document.querySelector('body')!;
    if (isMobileMenuVisible) {
      body.classList.add('overflow-hidden');
    } else {
      body.classList.remove('overflow-hidden');
    }
  }, [isMobileMenuVisible]);

  const stringToColor = (str: string): string => {
    const color = md5(str).slice(0, 4);
    return `#00${color}`;
  };

  const renderLogo = () => {
    if (tenantLogoConfig[subdomain]) {
      const { logoFile, showInsteadOfOriginalLogo } = tenantLogoConfig[subdomain];

      if (!showInsteadOfOriginalLogo) {
        return (
          <ImageWrapper>
            <Image
              src={logo}
              height={45}
            />
            <Image
              src={logoFile}
              height={45}
            />
          </ImageWrapper>
        );
      }

      return (
        <Image
          src={logoFile}
          height={45}
        />
      );
    }

    return (
      <Image
        src={logo}
        height={45}
      />
    );
  };

  const renderNav = () => {
    /**
     * !!!!!!!!!!!!!!!!!!!!!!!!
     *
     * If updating header nav here, please make sure
     * that you update mobile navigation as well
     *
     * !!!!!!!!!!!!!!!!!!!!!!!!
     */

    const isOperator = getSubdomain() === 'operators';

    return menuItems
      .filter(menuItem => menuItem.type !== 'button')
      .map(menuItem => {
        /**
         * If the operator is in client that doesn't have the
         * templates activated - don't show the settings menu
         */
        if (isOperator && menuItem.to === buildClientUrl('/settings', client) && !client?.templates) return null;
        if (isOperator && menuItem.to === buildClientUrl('/leaves', client) && !client?.leaves) return null;
        if (isOperator && menuItem.to === buildClientUrl('/hourly-absences', client) && !client?.is_hourly_absences)
          return null;
        if (
          isOperator &&
          menuItem.to === buildClientUrl('/health-and-safety-training', client) &&
          !client?.is_health_and_safety_training
        )
          return null;
        if (isOperator && menuItem.to === buildClientUrl('/work-trips', client) && !client?.is_work_trips) return null;
        if (menuItem.childrenRoutes && menuItem.childrenRoutes.length) {
          return (
            <Dropdown
              key={menuItem.label}
              {...menuItem}
            />
          );
        }

        if (menuItem.type === 'exit-client') {
          return (
            <div
              key={menuItem.label}
              className="d-flex ml-auto"
            >
              <ExitClientButton
                className="align-self-center mr-4"
                color={stringToColor(client!.id!.toString())}
                onClick={() => {
                  if (removeActiveClient) removeActiveClient();
                  history.push('/');
                }}
              >
                <menuItem.icon
                  size={14}
                  style={{ marginRight: '0.5rem' }}
                />
                {`${t(menuItem.label)} ${client?.id}`}
              </ExitClientButton>
            </div>
          );
        }

        return (
          <StyledNavLink
            key={menuItem.label}
            isActive={isActiveRoute(location, menuItem.to, client)}
          >
            <Link
              aria-label={menuItem.ariaLabel}
              to={menuItem.to}
              className="font-weight-normal nav-link mt-2 mt-md-0 mb-2 mb-md-0"
            >
              <menuItem.icon
                size={20}
                style={{ marginRight: '0.5rem' }}
              />
              {t(menuItem.label)}
            </Link>
          </StyledNavLink>
        );
      });
  };

  const renderButtons = () => {
    const isOperator = getSubdomain() === 'operators';

    return menuItems
      .filter(menuItem => menuItem.type === 'button')
      .map(menuItem => {
        const canCreateDoc = !isOperator && checkPermission(PermissionKeys.CAN_CREATE_DOCUMENTS);

        return (
          <TooltipWrapper
            key={menuItem.label}
            id="tooltip-create-doc"
            overlay={t(isOperator ? 'docWizard.operatorMessage' : 'common.noPermission')}
            placement="bottom"
            show={canCreateDoc ? false : undefined}
            wrapperClass="d-flex"
          >
            <Button
              className="align-self-center mr-4"
              disabled={!canCreateDoc}
              ariaLabel={menuItem.ariaLabel}
              onClick={() => {
                resetWizardState();
                history.push({
                  pathname: menuItem.to,
                  state: { ...(menuItem.to === '/documents-wizard/step-1' ? { isNew: 1 } : {}) },
                });
              }}
            >
              <menuItem.icon style={{ marginRight: '0.5rem' }} />
              {t(menuItem.label)}
            </Button>
          </TooltipWrapper>
        );
      });
  };

  const openSideMenu = () => {
    setIsMobileMenuVisible(true);
  };

  const closeSideMenu = () => {
    setIsMobileMenuVisible(false);
  };

  const { hasAvailableArchives, availableUndownloadedArchivesCount } = useArchiveData();

  return (
    <Header
      bg="white"
      expand="md"
      fixed="top"
    >
      <Navbar.Brand href={client?.id ? `/client/${client.id}` : '/'}>{renderLogo()}</Navbar.Brand>
      <ButtonsContainer>{renderButtons()}</ButtonsContainer>
      <ScrollableNav className="d-lg-flex flex-grow-1 d-none">{renderNav()}</ScrollableNav>

      {isClientAdmin && hasAvailableArchives && (
        <ArchiveButtonWrapper onClick={() => history.push(client?.id ? `/client/${client.id}/archives` : '/archives')}>
          <ArchiveButton
            className="d-none d-lg-flex mr-3"
            isActive={location.pathname.includes('/archives')}
          >
            <IconWrapper>
              <FaArchive size={20} />
              {availableUndownloadedArchivesCount !== undefined && availableUndownloadedArchivesCount > 0 && (
                <NotificationBadge>{availableUndownloadedArchivesCount}</NotificationBadge>
              )}
            </IconWrapper>
            <span style={{ marginLeft: '8px' }}>{t('archives.title')}</span>
          </ArchiveButton>
        </ArchiveButtonWrapper>
      )}

      {process.env.REACT_APP_SHOW_LANG_MENU === 'true' && showLangSwitch && (
        <LanguageSwitch className="d-none d-lg-block" />
      )}
      <Profile />
      <Hamburger
        aria-label="mobile-menu-open"
        className="d-lg-none d-block"
        onClick={openSideMenu}
      >
        <BsList size={25} />
      </Hamburger>
      <HeaderMobile
        menuItems={menuItems}
        isVisible={isMobileMenuVisible}
        onClose={closeSideMenu}
        showLangSwitch={showLangSwitch}
        isClientAdmin={isClientAdmin}
      />
    </Header>
  );
};

export default HeaderComponent;

const Header = styled(Navbar)`
  padding: 0.5rem 2rem;
  box-shadow: ${props => props.theme.boxShadow};
  display: flex;
  align-items: center;

  @media ${props => props.theme.breakpoints.lg} {
    padding: 0.5rem 1rem;
  }
`;

const ScrollableNav = styled(Nav)`
  display: flex;
  flex-grow: 1;
  margin-right: 1rem;

  &::-webkit-scrollbar {
    height: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #aaa;
    border-radius: 5px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;

  @media ${props => props.theme.breakpoints.lg} {
    display: none;
  }
`;

const StyledNavLink = styled.span<{ isActive: boolean }>`
  padding: 0 0.5rem !important;
  display: flex;
  align-items: center;

  a {
    color: ${props => (props.isActive ? props.theme.colors.violet : 'inherit')} !important;
  }

  &:hover {
    color: ${props => props.theme.colors.violet} !important;
  }

  @media ${props => props.theme.breakpoints.md} {
    padding: 0 !important;
  }
`;

const Hamburger = styled.button`
  background: none;
  border: none;
  padding: 0.75rem;
  margin-left: 1rem;
`;

const ExitClientButton = styled(Button)<{ color: string }>`
  background-color: ${props => props.color};
  border-color: ${props => props.color};

  &:hover {
    background-color: ${props => props.color};
    border-color: ${props => props.color};
    opacity: 0.7;
  }
`;

const ArchiveButton = styled.button<{ isActive: boolean }>`
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: ${props => (props.isActive ? props.theme.colors.violet : 'inherit')};
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${props => props.theme.colors.violet};
  }

  &:focus {
    outline: none;
  }
`;

const ArchiveButtonWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  align-self: center;
  cursor: pointer;
`;

const IconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  pointer-events: none;
`;

const ImageWrapper = styled.div`
  display: flex;
  gap: 1rem;

  img {
    max-width: 160px;
  }

  @media (max-width: 500px) {
    width: 50%;

    img {
      max-width: 90px;
    }
  }
`;
